import { useEffect, useState } from 'react';
import cls from 'classnames';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import getQuery from '~/utils/get-query';
import TestSymbol from '../test-symbol';
import { useAtomValue } from 'jotai';
import { PolicyPath, Lemon8Host } from 'shared/constants';
import { $LegalRegion, $LegalLocation } from 'shared/atoms';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { Language, Region, isEuropeRegion } from '@lemon8/web-app-shared/i18n';
import { checkOpenInBrowser, handleUrl } from '~/utils/webview-url';
import { useTitleVisible } from '~/utils/hooks/use-title-visible';
import { useLocation } from '@remix-run/react';
import { open } from '@bridge/sharee';

import 'intersection-observer';

import '~/styles/policy.scss';
import '~/styles/policy-inapp.scss';
import '~/styles/policy-mobile.scss';

import './index.scss';

const filterURL = (
  raw: string,
  legalRegion: Region | 'fr',
  regionLanguage: Language,
  reportUrl: string,
  origin: string,
): string => {
  const reportUrlQueryStr = reportUrl ? `&report_url=${encodeURIComponent(reportUrl)}` : '';
  const copyrightReportUrl = `${origin}/h5/feoffline/report/email-verify/input?region=${legalRegion}&language=${regionLanguage}${reportUrlQueryStr}&report_type=copyright&disable_ttnet_proxy=1`;
  const trademarkReportUrl = `${origin}/h5/feoffline/report/email-verify/input?region=${legalRegion}&language=${regionLanguage}${reportUrlQueryStr}&report_type=trademark&disable_ttnet_proxy=1`;
  const adequacyDecisionsUrl =
    'https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/adequacy-decisions_en';
  const getLawEnforcementFormUrl = (region: Region | 'fr'): string => {
    if (region === Region.us) {
      return 'https://law-enforcement-tiktok-uscan.zendesk.com/hc/en-us/requests/new';
    }
    if (isEuropeRegion(region as Region)) {
      return 'https://eu.safety-enforcement.tiktok.com/';
    }
    return 'https://safety-enforcement.tiktok.com/';
  };
  const lawEnforcementFormUrl = getLawEnforcementFormUrl(legalRegion);

  // can not use schema here, because ReactMarkdown cannot handle schema with <a href={schema}></a>
  // muse use whole url, otherwise ReactMarkdown handle schema maybe error
  return Object.keys(PolicyPath).reduce((str: string, k) => {
    return str
      .replace(
        new RegExp(`LEGAL_${k}`, 'g'),
        `${origin}${PolicyPath[k as keyof typeof PolicyPath]}?region=${legalRegion}&language=${regionLanguage}`,
      )
      .replace(new RegExp('MAIL_LEMON8', 'g'), 'mailto:IP@lemon8-app.com')
      .replace(new RegExp('REPORT_INFRINGEMENT_COPYRIGHT', 'g'), copyrightReportUrl)
      .replace(new RegExp('REPORT_INFRINGEMENT_TRADEMARK', 'g'), trademarkReportUrl)
      .replace(new RegExp('ADEQUACY_DECISIONS', 'g'), adequacyDecisionsUrl)
      .replace(new RegExp('LAW_ENFORCEMENT_FORM', 'g'), lawEnforcementFormUrl);
  }, raw);
};

export type TLegalMarkdownSrc = { [key in Region | 'fr']: any };

export default function MarkdownNode(props: {
  isMobile?: boolean;
  isInApp?: boolean;
  src: string | Partial<TLegalMarkdownSrc>;
}): JSX.Element {
  const { isMobile = false, isInApp = false, src = '' } = props;

  const { region } = useAtomValue($LegalRegion);
  const { language: regionLanguage, systemLanguage } = useAtomValue($Locale);
  const { hasNav = true, origin } = useAtomValue($LegalLocation);

  const location = useLocation();
  const isLegalTos = location.pathname.includes('/supplemental-terms-of-use');
  let legalRegion: Region | 'fr' = region;
  if (isLegalTos && region === Region.ca && systemLanguage === Language.fr) {
    legalRegion = 'fr';
  }

  const [reportUrl, setReportUrl] = useState<string>('');

  // 区分内测和线上域名，防止其他端同学挂错链接
  const [isTestEnv, setIsTestEnv] = useState<boolean>(false);

  useTitleVisible('.document-container h1');

  let content = '';
  if (typeof src !== 'string') {
    content = src[legalRegion];
  } else {
    content = src;
  }

  useEffect(() => {
    const urlHost = document.domain;
    if (
      urlHost.includes(Lemon8Host.TEST_ROW_SURFIX) ||
      urlHost.includes(Lemon8Host.TEST_VA_PREFIX) ||
      urlHost.includes(Lemon8Host.TEST_SG_PREFIX)
    ) {
      setIsTestEnv(true);
    }
    const original_report_url = getQuery('report_url') || '';
    const report_url = original_report_url ? decodeURIComponent(original_report_url) : '';
    setReportUrl(report_url);
  }, []);

  return (
    <>
      {isTestEnv && <TestSymbol />}
      <ReactMarkdown
        className={cls('document-container', {
          'platform-inapp': isInApp,
          'platform-mobile': isMobile,
          'has-nav': !isInApp && hasNav,
        })}
        rehypePlugins={[rehypeRaw as any]}
        remarkPlugins={[remarkGfm as any]}
        components={{
          // must handle the url when in app, other the webview back behavior maybe error
          a: (props: any) => {
            const { node, href = '', children, ...rest } = props;

            return (
              <a {...rest} href={handleUrl(region, href, isInApp)}>
                {children}
              </a>
            );
          },
        }}
      >
        {filterURL(content, legalRegion, regionLanguage, reportUrl, origin)}
      </ReactMarkdown>
    </>
  );
}
